import React, { ReactNode } from 'react';

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-pro';

import { Title } from '../../../@types/Calculations';
import { Province } from '../../../@types/KingdomResponse';
import { letterize } from '../../../helpers/NumberFormatting';
import CustomDetailPanelToggle from '../components/CustomDetailPanelToggle';
import {
  getAllIntelAge,
  getBadSpells,
  getGoodSpells,
  getArmy,
  getIntelAgeWithToolTip,
  getBuildingColName,
  simpleComparator,
  getCombo,
  getSciPercentage,
  getSurveyRenderValue,
  getRequests,
  getSurveyValue,
  getSciValue,
  colorCodedResult,
  getColorizedDefHomeWithTooltip,
  colorCodedLetterizedResult,
  percentageComparator,
  getGoodShortSpellNames,
  getBadShortSpellNames,
  getFirstArmySecondsFromParam,
  getObjectives,
  ranks,
  getAllArmy,
  getCooldown,
  getSurveyIncomingRenderValue,
  opComparator,
  colorCodedResultBuildingSpan,
} from '../helpers/colHelpers';

export type ValueGetterParams = { row: Province; value: any; field: string };

export const mandatoryCols: GridColDef[] = [
  { field: 'id', headerName: 'id', maxWidth: 0, hideable: true },
  { field: 'location', headerName: 'Kd #', hideable: true },
  {
    field: 'slot',
    headerName: '#',
    minWidth: 30,
    maxWidth: 40,
    hideable: true,
    resizable: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 100,
  },
  {
    field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
    headerName: '',
    headerClassName: () => 'toggle-col',
    cellClassName: () => 'toggle-col',
    renderCell: (params) => <CustomDetailPanelToggle {...params} />,
    hideable: false,
    sortable: false,
  },
  {
    field: 'ruler',
    headerName: 'Ruler',
    valueGetter: (value, row) => row.sot?.ruler,
  },
  {
    field: 'race',
    headerName: 'Race',
    valueGetter: (value, row) => row.race,
  },
  {
    field: 'personality',
    headerName: 'Personality',
    valueGetter: (value, row) => row.sot?.personality,
  },
  {
    field: 'combo',
    headerName: 'Combo',
    minWidth: 70,
    valueGetter: (value, row) => getCombo(row),
  },
  {
    field: 'discord',
    headerName: 'Discord',
    valueGetter: (value, row) => row.discord,
  },
  {
    field: 'note',
    headerName: 'Note',
    description: 'This column is set using OPs planner or Users (Own KD)',
    valueGetter: (value, row) => row.note,
  },
  {
    field: 'group',
    headerName: 'Group',
    description: 'This column is set using OPs planner or Users (Own KD)',
    valueGetter: (value, row) => row.group,
  },
  {
    field: 'honor',
    headerName: 'Honor',
    valueGetter: (value, row) => (row.title ? ranks[row.title as Title] : 0),
    renderCell: (params: GridRenderCellParams) => params.row.title,
  },
  {
    field: 'land',
    headerName: 'Acres',
    align: 'right',
    headerAlign: 'left',
    type: 'number',
    valueGetter: (value, row) => row.sot?.land ?? row.land,
  },
  {
    field: 'nw',
    headerName: 'NW',
    align: 'right',
    headerAlign: 'left',
    type: 'number',
    valueGetter: (value, row) => row.sot?.networth ?? row.networth,
    renderCell: (params: GridRenderCellParams) => (
      <div style={{ textTransform: 'lowercase' }}>
        {letterize(params.row.sot?.networth ?? params.row.networth)}
      </div>
    ),
    sortComparator: simpleComparator,
  },
  {
    field: 'nwpa',
    headerName: 'NWpa',
    align: 'right',
    headerAlign: 'left',
    type: 'number',
    valueGetter: (value, row) => row.sot?.nwpa ?? row.nwpa,
  },
];
export const map: GridColDef = {
  field: 'map',
  headerName: 'MAP',
  description: 'Multi-attack protection taken from SoT',
  valueGetter: (value, row) => row.sot?.map,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.sot?.map, params.row.sot?.ageSeconds),
};
export const tpa: GridColDef = {
  field: 'tpa',
  headerName: 'rTpa',
  description: 'Tpa calculated from Sot, or using most recent infiltrate',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.tpa,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.sot?.tpa,
      params.row.sot?.infilAgeSeconds ?? params.row.sot?.ageSeconds
    ),
};
export const tpaOff: GridColDef = {
  field: 'modOffTpa',
  headerName: 'oTpa',
  description: 'Modded Offensive TPA',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.calcs?.modOffTpa,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.calcs?.modOffTpa, params.row.sot?.ageSeconds),
};
export const tpaDef: GridColDef = {
  field: 'modDefTpa',
  headerName: 'dTpa',
  description: 'Modded Defensive TPA',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.calcs?.modDefTpa,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.calcs?.modDefTpa, params.row.sot?.ageSeconds),
};
export const wpa: GridColDef = {
  field: 'wpa',
  headerName: 'rWpa',
  description:
    'Wpa calculated from wizards, or estimated on enemy from SoT, SoS and survey',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.wpa,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.sot?.wpa, params.row.sot?.ageSeconds),
};
export const wpaOff: GridColDef = {
  field: 'modOffWpa',
  headerName: 'oWpa',
  description: 'Modded Offensive WPA',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.calcs?.modOffWpa,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.calcs?.modOffWpa, params.row.sot?.ageSeconds),
};
export const wpaDef: GridColDef = {
  field: 'modDefWpa',
  headerName: 'dWpa',
  description: 'Modded Defensive WPA',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.calcs?.modDefWpa,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.calcs?.modDefWpa, params.row.sot?.ageSeconds),
};
export const stealth: GridColDef = {
  field: 'stealth',
  headerName: 'Stlth',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.stealth ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.sot?.stealth ? `${params.row.sot.stealth}%` : null,
      params.row.sot?.ageSeconds
    ),
};
export const mana: GridColDef = {
  field: 'mana',
  headerName: 'Mana',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.mana ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.sot?.mana ? `${params.row.sot.mana}%` : null,
      params.row.sot?.ageSeconds
    ),
};
export const ovP: GridColDef = {
  field: 'ovP',
  headerName: 'Pop%',
  description: 'Calculated overpop from Sot, Survey, SoS',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.calcs?.oPop ?? null,
  sortComparator: simpleComparator,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.calcs?.oPop ? `${params.row.calcs.oPop}%` : null,
      Math.max(
        params.row.sot?.ageSeconds,
        params.row.som?.ageSeconds,
        params.row.survey?.ageSeconds
      )
    ),
};
export const army: GridColDef = {
  field: 'army',
  headerName: 'Army',
  align: 'center',
  valueGetter: (value, row) => getFirstArmySecondsFromParam(row),
  renderCell: (params: GridRenderCellParams) => getArmy(params),
};
export const armyAll: GridColDef = {
  field: 'armyAll',
  headerName: 'AllArmies',
  align: 'center',
  valueGetter: (value, row) => getFirstArmySecondsFromParam(row),
  renderCell: (params: GridRenderCellParams) => getAllArmy(params),
};
export const off: GridColDef = {
  field: 'off',
  headerName: 'Off',
  description: 'Total offense from SoT',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.offPoints ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.offPoints,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const def: GridColDef = {
  field: 'def',
  headerName: 'Def',
  description: 'Total defense from SoT',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.defPoints ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.defPoints,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const offHome: GridColDef = {
  field: 'offHome',
  headerName: 'OffHome',
  description: 'Offense home, taken from SoM',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.som?.offPointsHome ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.offPointsHome,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const defHome: GridColDef = {
  field: 'defHome',
  headerName: 'DefHome',
  description: 'Most recent reliable defense using SoD, SoM, SoT',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.calcs?.defPointsSummary?.defPointsHome,
  renderCell: (params: GridRenderCellParams) =>
    getColorizedDefHomeWithTooltip(params),
  sortComparator: simpleComparator,
};
export const somDef: GridColDef = {
  field: 'somDef',
  headerName: 'SomDef',
  description: 'Most recent SoM defense home',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.som?.defPointsHome ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.defPointsHome,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const osPoints: GridColDef = {
  field: 'osPoints',
  headerName: 'OS Off',
  description: 'OffSpecs on horses total offense',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.calcs?.mountedOffSpecsRaw ?? null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.calcs?.mountedOffSpecsRaw ?? null,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const dsPoints: GridColDef = {
  field: 'dsPoints',
  headerName: 'DS Def',
  description: 'DefSpecs total defense',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.calcs?.defSpecsRaw ?? null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.calcs?.defSpecsRaw ?? null,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const opa: GridColDef = {
  field: 'opa',
  headerName: 'Opa',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.opa ?? null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.sot?.opa ?? null, params.row.sot?.ageSeconds),
};
export const dpa: GridColDef = {
  field: 'dpa',
  headerName: 'Dpa',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.dpa ?? null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.sot?.dpa ?? null, params.row.sot?.ageSeconds),
};
export const ome: GridColDef = {
  field: 'ome',
  headerName: 'OME',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => (row.som?.ome ? `${row.som?.ome}%` : null),
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.som?.ome ? `${params.row.som?.ome}%` : null,
      params.row.som?.ageSeconds
    ),
};
export const dme: GridColDef = {
  field: 'dme',
  headerName: 'DME',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => (row.som?.dme ? `${row.som?.dme}%` : null),
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.som?.dme ? `${params.row.som?.dme}%` : null,
      params.row.som?.ageSeconds
    ),
};
export const peons: GridColDef = {
  field: 'peons',
  headerName: 'Peons',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.peasants,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.peasants,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const horses: GridColDef = {
  field: 'horses',
  headerName: 'Horses',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.horses,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.horses,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const solds: GridColDef = {
  field: 'solds',
  headerName: 'Solds',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.soldiers,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.soldiers,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const prisoners: GridColDef = {
  field: 'prisoners',
  headerName: 'Prisoners',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.prisoners,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.prisoners,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const ppa: GridColDef = {
  field: 'ppa',
  headerName: 'Ppa',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.ppa ?? null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.sot?.ppa ?? null, params.row.sot?.ageSeconds),
};
export const nonPeon: GridColDef = {
  field: 'nonPeon',
  headerName: 'NonPeon',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) =>
    row.som?.nonPeon ? `${row.som.nonPeon}%` : null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.som?.nonPeon ? `${params.row.som.nonPeon}%` : null,
      params.row.som?.ageSeconds
    ),
};
export const thieves: GridColDef = {
  field: 'thieves',
  headerName: 'Thv',
  description: 'Number of thieves from SoT or infiltrate',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.thieves,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.thieves,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const wizards: GridColDef = {
  field: 'wizards',
  headerName: 'Wiz',
  description:
    'Number of wizards from SoT or estimated on enemey using SoT, SoS and Survey',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.wizards,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.wizards,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const ospecs: GridColDef = {
  field: 'ospecs',
  headerName: 'OS',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.oSpecs,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.oSpecs,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const dspecs: GridColDef = {
  field: 'dspecs',
  headerName: 'DS',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.dSpecs,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.dSpecs,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const leets: GridColDef = {
  field: 'leets',
  headerName: 'Leets',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.elites,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.elites,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const ospecsTraining: GridColDef = {
  field: 'osTraining',
  headerName: 'OS Train',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.som?.training.oSpecs,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.training.oSpecs,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const dspecsTraining: GridColDef = {
  field: 'dsTraining',
  headerName: 'DS Train',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.som?.training.dSpecs,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.training.dSpecs,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const eliteTraining: GridColDef = {
  field: 'eliteTraining',
  headerName: 'Elite Train',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.som?.training.elites,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.training.elites,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const thievesTraining: GridColDef = {
  field: 'thiefTraining',
  headerName: 'Thief Train',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.som?.training.thieves,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.training.thieves,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const military: GridColDef = {
  field: 'military',
  headerName: 'Military',
  description: 'Total troops not including wizards or thieves',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.totalTroops ?? null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.totalTroops ?? null,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const be: GridColDef = {
  field: 'be',
  headerName: 'BE',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => (row.sot ? `${row.sot?.be}%` : null),
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.sot ? `${params.row.sot?.be}%` : null,
      params.row.sot?.ageSeconds
    ),
  sortComparator: percentageComparator,
};
export const wages: GridColDef = {
  field: 'wages',
  headerName: 'Wages',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.ma?.wages,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.ma?.wages ? `${params.row.ma?.wages}%` : '',
      params.row.som?.ageSeconds
    ),
};
export const draftTarget: GridColDef = {
  field: 'draftTarget',
  headerName: 'DrftTgt',
  align: 'center',
  headerAlign: 'left',
  valueGetter: (value, row) =>
    row.ma?.draftTarget ? `${row.ma.draftTarget}%` : null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.ma?.draftTarget ? `${params.row.ma.draftTarget}%` : null,
      params.row.som?.ageSeconds
    ),
};
export const draftRate: GridColDef = {
  field: 'draftRate',
  headerName: 'DrftRate',
  align: 'left',
  valueGetter: (value, row) => (row.ma?.draftRate ? row.ma.draftRate : ''),
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(
      params.row.ma?.draftRate ? params.row.ma.draftRate : '',
      params.row.som?.ageSeconds
    ),
};
export const requests: GridColDef = {
  field: 'requiredOps',
  description:
    'This column is set using OPs planner and shows the required ops to be performed. Duration spell requests are hidden automatically once the spell is active.',
  headerName: 'OPs ToDo',
  cellClassName: () => 'spell-col',
  renderCell: (params: GridRenderCellParams) => getRequests(params),
  sortComparator: opComparator,
};
export const objectives: GridColDef = {
  field: 'requiredObjectives',
  description:
    'Objectives set using OPs planner and shows the target value on the enemy province',
  headerName: 'Objectives',
  valueGetter: (value, row) => getObjectives(row),
};
export const badSpells: GridColDef = {
  field: 'badSpells',
  headerName: 'BadSpells',
  cellClassName: () => 'spell-col',
  valueGetter: (value, row) => getBadShortSpellNames(row),
  renderCell: (params: GridRenderCellParams) => getBadSpells(params),
};
export const goodSpells: GridColDef = {
  field: 'goodSpells',
  headerName: 'GoodSpells',
  cellClassName: () => 'spell-col',
  valueGetter: (value, row) => getGoodShortSpellNames(row),
  renderCell: (params: GridRenderCellParams) => getGoodSpells(params),
};

export const trainCredits: GridColDef = {
  field: 'creditsTrain',
  headerName: 'T-Credits',
  align: 'right',
  headerAlign: 'left',
  valueGetter: (value, row) => row.ma?.credits,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.ma?.credits,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const buildCredits: GridColDef = {
  field: 'creditsBuild',
  headerName: 'B-Credits',
  align: 'right',
  headerAlign: 'left',
  valueGetter: (value, row) => row.survey?.credits,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.survey?.credits,
      params.row.survey?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const gcpa: GridColDef = {
  field: 'gcpa',
  headerName: 'GCpa',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.gcpa ?? null,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedResult(params.row.sot?.gcpa ?? null, params.row.sot?.ageSeconds),
};
export const gcs: GridColDef = {
  field: 'gcs',
  headerName: 'GCs',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.money,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.money,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const food: GridColDef = {
  field: 'food',
  headerName: 'Food',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.food,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.food,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const runes: GridColDef = {
  field: 'runes',
  headerName: 'Runes',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.runes,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.runes,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const tb: GridColDef = {
  field: 'tb',
  headerName: 'TB',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (value, row) => row.sot?.tb,
  renderCell: (params: GridRenderCellParams) =>
    colorCodedLetterizedResult(params.row.sot?.tb, params.row.sot?.ageSeconds),
  sortComparator: simpleComparator,
};
export const coolDown: GridColDef = {
  field: 'cd',
  headerName: 'Unique Cooldown',
  align: 'left',
  headerAlign: 'left',
  type: 'string',
  renderCell: (params: GridRenderCellParams) => getCooldown(params),
};

export const getSciences = (sciences: string[]) => {
  const scienceArr: GridColDef[] = [
    {
      field: 'stockedBooks',
      headerName: 'Stocked',
      align: 'right',
      headerAlign: 'left',
      type: 'number',
      valueGetter: (value, row) => row?.sos?.unallocatedBooks,
      renderCell: (params: GridRenderCellParams) =>
        colorCodedLetterizedResult(
          params.row?.sos?.unallocatedBooks,
          params.row?.sos?.ageSeconds
        ),
    },
    {
      field: 'allocatedBooks',
      headerName: 'Allocated',
      align: 'right',
      headerAlign: 'left',
      type: 'number',
      valueGetter: (value, row) => row?.sos?.allocatedBooks,
      renderCell: (params: GridRenderCellParams) =>
        colorCodedLetterizedResult(
          params.row?.sos?.allocatedBooks,
          params.row?.sos?.ageSeconds
        ),
    },
  ];
  sciences.forEach((s, i) => {
    scienceArr.push({
      field: `boo*${i}`,
      headerName: s.substring(0, 5),
      align: 'right',
      headerAlign: 'left',
      type: 'number',
      valueGetter: (value, row) => getSciValue(row, s),
      renderCell: (params: GridRenderCellParams) =>
        colorCodedResult(
          getSciPercentage(params, s),
          params.row.sos?.ageSeconds
        ),
      sortComparator: simpleComparator,
    });
  });
  scienceArr.push({
    field: 'sosAge',
    headerName: 'Sos',
    type: 'number',
    valueGetter: (value, row) => row?.sos?.ageSeconds ?? 0,
    renderCell: (params: GridRenderCellParams) =>
      getIntelAgeWithToolTip(params.row?.sos?.ageSeconds, 'SOS'),
    sortComparator: simpleComparator,
  });
  return scienceArr;
};

export const getBuildings = (builds: string[], isMobileOnly: boolean) => {
  const buildsArr: GridColDef[] = [];
  builds.forEach((s, i) => {
    buildsArr.push({
      field: `bld*${i}`,
      headerName: getBuildingColName(s),
      headerAlign: 'left',
      type: 'number',
      align: 'left',
      minWidth: isMobileOnly ? 70 : undefined,
      valueGetter: (value, row) => getSurveyValue(row, s),
      renderCell: (params: GridRenderCellParams) => {
        return colorCodedResultBuildingSpan(
          getSurveyRenderValue(params, s),
          getSurveyIncomingRenderValue(params, s),
          params.row.survey?.ageSeconds
        );
      },
      sortComparator: simpleComparator,
    });
  });
  buildsArr.push({
    field: 'surveyAge',
    headerName: 'Sur',
    type: 'number',
    valueGetter: (value, row) => row?.survey?.ageSeconds ?? 0,
    renderCell: (params: GridRenderCellParams) =>
      getIntelAgeWithToolTip(params.row?.survey?.ageSeconds, 'Survey'),
    sortComparator: simpleComparator,
  });
  return buildsArr;
};

export const intelAgeCols: GridColDef = {
  field: 'intelAge',
  headerName: 'IntelAge',
  type: 'number',
  valueGetter: (value, row) => row?.sot?.ageSeconds,
  renderCell: (params: GridRenderCellParams) => getAllIntelAge(params),
  sortComparator: simpleComparator,
};

export const sotAge: GridColDef = {
  field: 'sotAge',
  headerName: 'Sot',
  type: 'number',
  valueGetter: (value, row) => row?.sot?.ageSeconds ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    getIntelAgeWithToolTip(params.row?.sot?.ageSeconds, 'SOT'),
  sortComparator: simpleComparator,
};

export const somAge: GridColDef = {
  field: 'somAge',
  headerName: 'Som',
  type: 'number',
  valueGetter: (value, row) => row?.som?.ageSeconds ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    getIntelAgeWithToolTip(params.row?.som?.ageSeconds, 'SOM'),
  sortComparator: simpleComparator,
};

export const infilAge: GridColDef = {
  field: 'infilAge',
  headerName: 'Infil',
  type: 'number',
  valueGetter: (value, row) => row?.sot?.infilAgeSeconds ?? 0,
  renderCell: (params: GridRenderCellParams) =>
    getIntelAgeWithToolTip(params.row?.sot?.infilAgeSeconds, 'Infiltrate'),
  sortComparator: simpleComparator,
};
